import { BaseModuleI, ModuleI, OptionI } from "../../interfaces";
import { getAllModulesByProfileId } from "../../apis";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useModulesBackupStore } from "./router-backup.store";

type State = {
  isLoading: boolean;
  section: string;
  previousModuleBase: string;
  options: OptionI[];
  modules: ModuleI[];
  moduleMap: Record<string, BaseModuleI>;
  profileId: string;
  avatar: string;
  selectClient: number | null;
  cloneCatalogue: number | null;
  cloneOption: number | null;
  cloneTask: number | null;
  fullName: string;
  profile: string;
  firstName: string;
  clientId: number | null;
};

type Actions = {
  setProfile: (profile: string) => void;
  setPreviousModuleBase: (
    previousModuleBase: State["previousModuleBase"]
  ) => void;
  setLoading: (loading: State["isLoading"]) => void;
  validateModulesFromApi: () => Promise<any>;
  setSection: (section: State["section"]) => void;
  setProfileId: (profileId: string) => void;
  setOptions: (options: OptionI[]) => void;
  setOptionsFirst: (options: OptionI[]) => void;
  setModules: (modules: ModuleI[]) => void;
  setAvatar: (avatar: string) => void;
  reset: () => void;
  resetOptions: () => void;
  resetModules: () => void;
  getModulesFromApi: () => void;
  setSelectClient: (client: number | null) => void;
  setCloneCatalogue: (clone: number | null) => void;
  setCloneOption: (clone: number | null) => void;
  setCloneTask: (task: State["cloneTask"]) => void;
  setFullName: (fullName: string) => void;
  setFirstName: (firstName: string) => void;
  setClientId: (clientId: number | null) => void;
};

const buildModuleMap = (
  modules: BaseModuleI[],
  map: Record<string, BaseModuleI> = {}
): Record<string, BaseModuleI> => {
  for (const module of modules) {
    if (module.to) {
      map[module.to] = module;
    }
    if (module.children && module.children.length > 0) {
      buildModuleMap(module.children, map);
    }
  }
  return map;
};

const initialState: State = {
  firstName: "",
  profile:"",
  fullName: "",
  section: "",
  isLoading: false,
  previousModuleBase: "",
  options: [],
  modules: [],
  moduleMap: {},
  profileId: "",
  avatar: "",
  selectClient: null,
  cloneCatalogue: null,
  cloneOption: null,
  cloneTask: null,
  clientId: null,
};

export const useRouterStore = create<State & Actions>()(
  persist(
    (set, get) => ({
      clientId: null,
      firstName: "",
      fullName: "",
      section: "",
      profile:"",
      isLoading: false,
      previousModuleBase: "",
      options: [],
      modules: [],
      moduleMap: {},
      profileId: "",
      avatar: "",
      selectClient: null,
      cloneCatalogue: null,
      cloneOption: null,
      cloneTask: null,
      setClientId: (clientId) => set((state) => ({ clientId })),
      setFirstName: (firstName) => set((state) => ({ firstName })),
      setProfile: (profile) => set((state) => ({ profile })),
      setFullName: (fullName) => set((state) => ({ fullName })),
      reset: () => set(initialState),
      resetOptions: () => set((state) => ({ options: [] })),
      resetModules: () => set((state) => ({ modules: [] })),
      validateModulesFromApi: async () => {
        set({ isLoading: true });
        try {
          const tokenData = JSON.parse(
            localStorage.getItem(process.env.REACT_APP_TOKEN!) || "{}"
          );
          const token = tokenData?.token ?? null;
          const profileId = tokenData?.user?.profileId ?? get().profileId;

          if (!token || !profileId) {
            console.error("No se encontró un token o profileId");
            set({ isLoading: false });
            return;
          }

          const { data: dataModules } = await getAllModulesByProfileId(
            profileId
          );

          if (dataModules) {
            const opts = Array.from(
              new Map(
                dataModules
                  .flatMap((mod) => mod.options || [])
                  .map((option) => [option.id, option]) // Usar `id` como clave única
              ).values()
            );
            const moduleMap = buildModuleMap(dataModules);
            set({
              modules: dataModules,
              options: opts,
              moduleMap,
            });
            useModulesBackupStore.getState().setModulesBackup(dataModules);
          } else {
            set({ modules: [], options: [] });
          }
          // return Promise.resolve(dataModules);
          return dataModules;
        } catch (error: any) {
          const { modulesBackup } = useModulesBackupStore.getState();
          if (!navigator.onLine || error.response?.status === 401) {
            if (modulesBackup.length > 0) {
              const opts = Array.from(
                new Map(
                  modulesBackup
                    .flatMap((mod) => mod.options || [])
                    .map((option) => [option.id, option])
                ).values()
              );
              set({
                modules: modulesBackup,
                options: opts,
              });
              return modulesBackup;
            }
            set({ modules: [], options: [] });
            return;
          }
          set({ modules: [], options: [] });
          return;
        } finally {
          set({ isLoading: false }); // Finaliza la carga
        }
      },
      getModulesFromApi: async () => {
        // const itRouter = localStorage.getItem(process.env.REACT_APP_TOKEN!);
        // if (!itRouter) {
        //   localStorage.removeItem(process.env.REACT_APP_TOKEN_ROUTER!);
        //   get().reset();
        //   return;
        // }
        // if (!get().profileId) return;
        // const { data: dataModules } = await getAllModulesByProfileId(
        //   get().profileId!
        // );
        // get().setModules(dataModules!);
      },
      setOptions: (options) => set({ options }),
      setModules: (modules) => set({ modules }),
      setPreviousModuleBase: (previousModuleBase) =>
        set((state) => ({ previousModuleBase })),
      setLoading: (loading) => set({ isLoading: loading }),
      setSection: (section) => set(() => ({ section })),
      setOptionsFirst: (options) => set((state) => ({ options })),
      setProfileId: (profileId) => set((state) => ({ profileId })),
      setAvatar: (avatar) => set((state) => ({ avatar })),
      setSelectClient: (selectClient) => set((state) => ({ selectClient })),
      setCloneCatalogue: (cloneCatalogue) =>
        set((state) => ({ cloneCatalogue })),
      setCloneOption: (cloneOption) => set((state) => ({ cloneOption })),
      setCloneTask: (cloneTask) => set((state) => ({ cloneTask })),
    }),
    { name: process.env.REACT_APP_TOKEN_ROUTER! }
  )
);
